import {
  useEffect, useState, memo, useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router';
import ROUTE from 'src/utils/routeConstants';
import { useDispatch, useSelector } from 'react-redux';
import { TRANSACTION_TYPES, USER_ROLES } from 'src/utils/constants';
import {
  getTransactionList,
} from 'src/redux/transactionSlice';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, transactionTableHeaders } from 'src/utils/tableHeaders';
import {
  getFormattedDate,
  getFuelQuantity,
  getTransactionQueryParams,
  hasAdminLikeAccess,
  removeLeadingZeros,
} from 'src/utils/getInitials';
import useAuth from 'src/context/AuthContext';
import CustomPagination from 'src/common/CustomPagination';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';
import TableColumnVisibilityMenu, { useTableColumnPreferences } from 'src/common/TableColumnVisibilityMenu';
import SiteDetailsDialog from 'src/components/cards/SiteDetailsDialog';
import VehicleDetailsDialog from 'src/components/cards/VehicleDetailsDialog';
import { getDispenserDisplayValue } from './transactionService';
import DispenserDetailsDialog from './DispenserDetailsDialog';

const DEFAULT_TRANSACTION_COLUMN_WIDTH = 140;
const TRANSACTION_COLUMN_WIDTHS = {
  action: 96,
  address: 190,
  delivery_date: 170,
  end_time: 170,
  order_date: 170,
  site_name: 180,
  start_time: 170,
  vehicle_category: 160,
  vehicle_model: 160,
  vehicle_number: 150,
};

const getTransactionTableMinWidth = (columns, isTablet) => {
  const columnWidth = columns.reduce(
    (total, column) => total + (TRANSACTION_COLUMN_WIDTHS[column.id] || DEFAULT_TRANSACTION_COLUMN_WIDTH),
    0
  );
  return Math.max(isTablet ? 900 : 1100, columnWidth);
};

const getRelatedEntityId = (value) => (
  value && typeof value === 'object' ? value.id : value
);

const EntityValue = ({
  value, entityId, entityType, onOpen, variant = 'body2', sx
}) => {
  const displayValue = value || 'N/A';
  const isClickable = entityId !== null && entityId !== undefined && displayValue !== 'N/A';
  const text = (
    <Typography
      variant={variant}
      sx={{
        color: 'inherit',
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {displayValue}
    </Typography>
  );

  if (!isClickable) return text;

  return (
    <Box
      component="button"
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen(entityType, entityId, displayValue);
      }}
      sx={{
        background: 'transparent',
        border: 0,
        color: 'inherit',
        cursor: 'pointer',
        display: 'block',
        maxWidth: '100%',
        m: 0,
        p: 0,
        textAlign: 'inherit',
        '&:hover': { textDecoration: 'underline' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      {text}
    </Box>
  );
};

EntityValue.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityType: PropTypes.oneOf(['site', 'vehicle', 'dispenser']).isRequired,
  onOpen: PropTypes.func.isRequired,
  variant: PropTypes.string,
  sx: PropTypes.object,
};

// Mobile Card View Component for Purchase Transactions
const PurchaseMobileCard = memo(({
  transaction, onViewClick, onEntityClick, theme, disabled
}) => (
  <Card
    sx={{
      mb: 2,
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      background: 'linear-gradient(180deg, #fff, #f9f9f9)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: `1px solid ${theme.palette.divider}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
    }}
  >
    {/* Header Section */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            mb: 0.5,
          }}
        >
          ID:
          {' '}
          {transaction.id}
        </Typography>
        <Box sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
          <EntityValue
            value={transaction.site_name}
            entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
            entityType="site"
            onOpen={onEntityClick}
          />
        </Box>
      </Box>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {/* Dates Grid */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Order Date
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {getFormattedDate(transaction.order_date) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Delivery Date
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {getFormattedDate(transaction.delivery_date) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Invoice Number
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.invoice_number || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Supplier
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.supplier || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Received Tank
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.received_tank_name || 'N/A'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* Quantity & Price Stats */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={6}>
        <Box
          sx={{
            textAlign: 'center',
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.08)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Quantity
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
            }}
          >
            {getFuelQuantity(
              transaction.actual_filled || transaction.quantity,
              transaction.actual_filled_gallons || transaction.quantity_gallons
            ) || '0 L'}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box
          sx={{
            textAlign: 'center',
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.success.lighter || 'rgba(46, 125, 50, 0.08)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Price/Litre
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.success.main,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
            }}
          >
            ₹
            {transaction.price_per_litre || '0'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* View Button */}
    <Button
      variant="contained"
      fullWidth
      disabled={disabled}
      sx={{
        borderRadius: 2,
        textTransform: 'none',
        py: { xs: 1, sm: 1.25 },
        fontSize: { xs: '0.875rem', sm: '0.938rem' },
        fontWeight: 600,
        background: 'linear-gradient(90deg, #1976d2, #115293)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        '&:hover': {
          background: 'linear-gradient(90deg, #115293, #1976d2)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        },
      }}
      onClick={() => onViewClick(transaction.id)}
    >
      View Details
    </Button>
  </Card>
));

PurchaseMobileCard.displayName = 'PurchaseMobileCard';

PurchaseMobileCard.propTypes = {
  transaction: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onEntityClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

// Mobile Card View Component for Sales Transactions
const SalesMobileCard = memo(({
  transaction, onViewClick, onEntityClick, theme, disabled
}) => (
  <Card
    sx={{
      mb: 2,
      p: { xs: 2, sm: 2.5 },
      borderRadius: 3,
      background: 'linear-gradient(180deg, #fff, #f9f9f9)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: `1px solid ${theme.palette.divider}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
    }}
  >
    {/* Header Section */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            mb: 0.5,
          }}
        >
          Order:
          {' '}
          {transaction.order_id || 'N/A'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: '0.813rem', sm: '0.875rem' },
          }}
        >
          {getFormattedDate(transaction.start_time) || 'N/A'}
        </Typography>
      </Box>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {/* Transaction Details */}
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Site & Location
          </Typography>
          <EntityValue
            value={transaction.site_name}
            entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
            entityType="site"
            onOpen={onEntityClick}
            sx={{ color: theme.palette.text.primary, mb: 0.5 }}
          />
          <EntityValue
            value={getDispenserDisplayValue(transaction)}
            entityId={transaction.dispenser_id}
            entityType="dispenser"
            onOpen={onEntityClick}
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          />
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Nozzle
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.nozzle_num || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Card 1
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {removeLeadingZeros(transaction.card_num_1) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Card 2
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {removeLeadingZeros(transaction.card_num_2) || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Vehicle Number
          </Typography>
          <EntityValue
            value={transaction.vehicle_number}
            entityId={transaction.vehicle_id}
            entityType="vehicle"
            onOpen={onEntityClick}
            sx={{ color: theme.palette.text.primary }}
          />
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Vehicle Model
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.vehicle_model || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={6} sm={4}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Vehicle Category
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.vehicle_category || 'N/A'}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              display: 'block',
              mb: 0.5,
            }}
          >
            Odometer Reading
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
          >
            {transaction.odometer_entry ?? 'N/A'}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* Quantity Badge */}
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.08)',
        textAlign: 'center',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 600,
          display: 'block',
          mb: 0.5,
        }}
      >
        Quantity Dispensed
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: theme.palette.primary.main,
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
        }}
      >
        {getFuelQuantity(transaction.quantity, transaction.quantity_gallons) || '0 L'}
      </Typography>
    </Box>

    {/* View Button */}
    <Button
      variant="contained"
      fullWidth
      disabled={disabled}
      sx={{
        borderRadius: 2,
        textTransform: 'none',
        py: { xs: 1, sm: 1.25 },
        fontSize: { xs: '0.875rem', sm: '0.938rem' },
        fontWeight: 600,
        background: 'linear-gradient(90deg, #1976d2, #115293)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        '&:hover': {
          background: 'linear-gradient(90deg, #115293, #1976d2)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        },
      }}
      onClick={() => onViewClick(transaction.id)}
    >
      View Details
    </Button>
  </Card>
));

SalesMobileCard.displayName = 'SalesMobileCard';

SalesMobileCard.propTypes = {
  transaction: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onEntityClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

const TransactionListTable = ({
  isSales,
  refreshData,
  searchText,
  dateFilter,
  siteFilter,
  tankFilter,
  transactionTypeOverride,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const user = getUser();
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const isApprover = user?.role === USER_ROLES.approver;
  const {
    transactionList,
    transactionType,
    totalCount,
    loading,
  } = useSelector((state) => state.transactions);
  const effectiveTransactionType = transactionTypeOverride || transactionType;

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('order_id');
  const [entityDialog, setEntityDialog] = useState(null);
  const activeTransactionHeaders = useMemo(
    () => transactionTableHeaders[effectiveTransactionType] || [],
    [effectiveTransactionType]
  );
  const {
    hiddenColumnIds,
    visibleColumns: visibleTransactionHeaders,
    onToggleColumn
  } = useTableColumnPreferences(`transactions_${effectiveTransactionType}`, activeTransactionHeaders);
  const transactionAlignById = useMemo(
    () => getAlignByColumnId(visibleTransactionHeaders),
    [visibleTransactionHeaders]
  );
  const transactionTableMinWidth = useMemo(
    () => getTransactionTableMinWidth(visibleTransactionHeaders, isTablet),
    [isTablet, visibleTransactionHeaders]
  );

  useEffect(() => {
    const orderById = effectiveTransactionType === TRANSACTION_TYPES[0].id ? 'order_id' : 'id';
    setOrderBy(orderById);
  }, [effectiveTransactionType]);

  useEffect(() => {
    setPage(0);
    dispatch(
      getTransactionList({
        transactionType: effectiveTransactionType,
        limit,
        pageNo: 1,
        params: getTransactionQueryParams(
          isSales,
          searchText,
          dateFilter,
          siteFilter,
          effectiveTransactionType,
          tankFilter
        ),
      })
    );
  }, [
    dispatch,
    effectiveTransactionType,
    limit,
    refreshData,
    isSales,
    searchText,
    dateFilter,
    siteFilter,
    tankFilter
  ]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(0);
    setLimit(newLimit);
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
    dispatch(
      getTransactionList({
        transactionType: effectiveTransactionType,
        limit,
        pageNo: newPage + 1,
        params: getTransactionQueryParams(
          isSales,
          searchText,
          dateFilter,
          siteFilter,
          effectiveTransactionType,
          tankFilter
        ),
      })
    );
  };

  const viewTransactionInfo = (transactionId) => {
    history.push(`${ROUTE.transactions}/${transactionId}?type=${effectiveTransactionType}`);
  };

  const openEntityDialog = (entityType, entityId, label) => {
    if (entityId === null || entityId === undefined) return;
    setEntityDialog({ entityType, entityId, label });
  };

  const closeEntityDialog = () => setEntityDialog(null);

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const isDetailsDisabled = () => {
    let disableDetails = !hasAdminAccess;
    if (effectiveTransactionType === TRANSACTION_TYPES[1].id && isApprover) {
      disableDetails = false;
    }
    return disableDetails;
  };

  const renderTableData = (transaction) => {
    const isTransferType = effectiveTransactionType === TRANSACTION_TYPES[2].id;
    const renderQuantity = (value) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: { sm: '0.875rem', md: '0.938rem' },
          }}
        >
          {getFuelQuantity(value, transaction.quantity_gallons)}
        </Typography>
      </Box>
    );

    const getCellValue = (columnId) => {
      if (effectiveTransactionType === TRANSACTION_TYPES[1].id) {
        const inwardValues = {
          id: transaction.id,
          order_date: getFormattedDate(transaction.order_date),
          delivery_date: getFormattedDate(transaction.delivery_date),
          site_name: (
            <EntityValue
              value={transaction.site_name}
              entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
              entityType="site"
              onOpen={openEntityDialog}
            />
          ),
          invoice_number: transaction.invoice_number,
          quantity: renderQuantity(transaction.actual_filled || transaction.quantity),
          price_per_litre: transaction.price_per_litre,
          supplier: transaction.supplier,
          source: transaction.source === 'sensor'
            ? `${transaction.source_provider || 'Sensor'}`
            : 'Manual',
          received_tank_name: transaction.received_tank_name || 'N/A'
        };
        return inwardValues[columnId] ?? transaction[columnId] ?? 'N/A';
      }

      if (effectiveTransactionType === TRANSACTION_TYPES[3].id) {
        const externalValues = {
          id: transaction.id,
          date_time: getFormattedDate(transaction.date_time),
          site_name: (
            <EntityValue
              value={transaction.site_name}
              entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
              entityType="site"
              onOpen={openEntityDialog}
            />
          ),
          dispenser_name: (
            <EntityValue
              value={transaction.dispenser_name}
              entityId={transaction.dispenser_id}
              entityType="dispenser"
              onOpen={openEntityDialog}
            />
          ),
          vehicle_number: (
            <EntityValue
              value={transaction.vehicle_number}
              entityId={transaction.vehicle_id}
              entityType="vehicle"
              onOpen={openEntityDialog}
            />
          ),
          odometer_entry: transaction.odometer_entry ?? 'N/A',
          quantity: getFuelQuantity(Number(transaction.quantity || 0), transaction.quantity_gallons),
          unit_price: transaction.unit_price ?? 'N/A',
          amount: transaction.amount ?? 'N/A'
        };
        return externalValues[columnId] ?? transaction[columnId] ?? 'N/A';
      }

      const transactionValues = {
        order_id: transaction.order_id,
        end_time: getFormattedDate(transaction.start_time),
        site_name: (
          <EntityValue
            value={transaction.site_name}
            entityId={getRelatedEntityId(transaction.site_id ?? transaction.site)}
            entityType="site"
            onOpen={openEntityDialog}
          />
        ),
        address: (
          <EntityValue
            value={getDispenserDisplayValue(transaction)}
            entityId={transaction.dispenser_id}
            entityType="dispenser"
            onOpen={openEntityDialog}
          />
        ),
        nozzle_num: transaction.nozzle_num,
        card_num_1: isTransferType ? removeLeadingZeros(transaction.card_num_1) || 'N/A' : transaction.card_num_1,
        card_num_2: isTransferType ? removeLeadingZeros(transaction.card_num_2) || 'N/A' : transaction.card_num_2,
        vehicle_number: (
          <EntityValue
            value={transaction.vehicle_number}
            entityId={transaction.vehicle_id}
            entityType="vehicle"
            onOpen={openEntityDialog}
          />
        ),
        vehicle_model: transaction.vehicle_model || 'N/A',
        vehicle_category: transaction.vehicle_category || 'N/A',
        odometer_entry: transaction.odometer_entry ?? 'N/A',
        quantity: renderQuantity(transaction.quantity),
        opening_stock: transaction.opening_stock == null
          ? 'None'
          : getFuelQuantity(transaction.opening_stock, transaction.opening_stock_gallons),
        closing_stock: transaction.closing_stock == null
          ? 'None'
          : getFuelQuantity(transaction.closing_stock, transaction.closing_stock_gallons)
      };
      return transactionValues[columnId] ?? transaction[columnId] ?? 'N/A';
    };

    return visibleTransactionHeaders
      .filter((column) => column.id !== 'action')
      .map((column) => (
        <TableCell
          align={transactionAlignById[column.id]}
          key={column.id}
          sx={{ fontSize: { sm: '0.813rem', md: '0.875rem' } }}
        >
          {getCellValue(column.id)}
        </TableCell>
      ));
  };

  // Get sorted data
  const displayData = transactionList?.slice().sort(getComparator(order, orderBy)) || [];

  // Determine which mobile card to use
  const isPurchaseType = effectiveTransactionType === TRANSACTION_TYPES[1].id;
  const hasActiveFilter = Boolean(
    dateFilter
    || (siteFilter && siteFilter !== 'all')
    || (tankFilter && tankFilter !== 'all')
  );
  const emptyStateMessage = hasActiveFilter
    ? 'No transactions found for the selected filters'
    : 'No transactions found';

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Mobile View: Card Layout */}
          {isMobile ? (
            <Box sx={{ width: '100%' }}>
              {displayData.length > 0 ? (
                displayData.map((transaction) => (isPurchaseType ? (
                  <PurchaseMobileCard
                    key={transaction.id}
                    transaction={transaction}
                    onViewClick={viewTransactionInfo}
                    onEntityClick={openEntityDialog}
                    theme={theme}
                    disabled={isDetailsDisabled()}
                  />
                ) : (
                  <SalesMobileCard
                    key={transaction.id}
                    transaction={transaction}
                    onViewClick={viewTransactionInfo}
                    onEntityClick={openEntityDialog}
                    theme={theme}
                    disabled={isDetailsDisabled()}
                  />
                )))
              ) : (
                <Card sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {emptyStateMessage}
                  </Typography>
                </Card>
              )}
              <CustomPagination
                page={page}
                rowsPerPage={limit}
                count={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
              />
            </Box>
          ) : (
            /* Desktop/Tablet View: Table Layout */
            <Card
              sx={{
                borderRadius: 3,
                background: '#FFFFFF',
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  px: 1.5,
                  py: 0.75
                }}
              >
                <TableColumnVisibilityMenu
                  columns={activeTransactionHeaders}
                  hiddenColumnIds={hiddenColumnIds}
                  onToggleColumn={onToggleColumn}
                />
              </Box>
              <Box
                sx={{
                  minHeight: 0,
                  flex: 1,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: '#F9FAFB',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    zIndex: 2
                  },
                  '& .MuiTableCell-root': {
                    minWidth: DEFAULT_TRANSACTION_COLUMN_WIDTH,
                    whiteSpace: 'nowrap'
                  },
                  '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                    position: 'sticky',
                    right: 0,
                    minWidth: TRANSACTION_COLUMN_WIDTHS.action,
                    width: TRANSACTION_COLUMN_WIDTHS.action,
                    zIndex: 3,
                    backgroundColor: '#F9FAFB',
                    boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <Box sx={{ minWidth: transactionTableMinWidth }}>
                  <Table stickyHeader sx={{ tableLayout: 'auto', minWidth: transactionTableMinWidth }}>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      headCells={visibleTransactionHeaders}
                      onRequestSort={handleRequestSort}
                      sx={{
                        backgroundColor: '#F9FAFB',
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <TableBody>
                      {displayData.length > 0 ? (
                        displayData.map((transaction) => (
                          <TableRow
                            hover
                            key={transaction.id}
                            sx={{
                              transition: 'background-color 0.3s, box-shadow 0.3s',
                              '&:hover': {
                                backgroundColor: '#f5f7fa',
                                boxShadow: theme.shadows[2],
                              },
                            }}
                          >
                            {renderTableData(transaction)}
                            <TableCell
                              align={transactionAlignById.action}
                              sx={{
                                position: 'sticky',
                                right: 0,
                                zIndex: 1,
                                backgroundColor: '#FFFFFF',
                                boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                              }}
                            >
                              <Tooltip title="View Details">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    viewTransactionInfo(transaction.id);
                                  }}
                                  disabled={isDetailsDisabled()}
                                  size="medium"
                                  aria-label="View details"
                                  sx={getTableActionIconButtonSx(theme)}
                                >
                                  <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={visibleTransactionHeaders.length || 1}
                            align="center"
                            sx={{ py: 4 }}
                          >
                            <Typography variant="body1" color="text.secondary">
                              {emptyStateMessage}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
              <Divider sx={{ flexShrink: 0 }} />
              <Box sx={{ flexShrink: 0, bgcolor: '#FFFFFF' }}>
                <CustomPagination
                  page={page}
                  rowsPerPage={limit}
                  count={totalCount}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                />
              </Box>
            </Card>
          )}
        </>
      )}

      {entityDialog?.entityType === 'site' && (
        <SiteDetailsDialog
          open
          onClose={closeEntityDialog}
          siteId={entityDialog.entityId}
          siteName={entityDialog.label}
        />
      )}
      {entityDialog?.entityType === 'vehicle' && (
        <VehicleDetailsDialog
          open
          onClose={closeEntityDialog}
          vehicleId={entityDialog.entityId}
          vehicleNumber={entityDialog.label}
        />
      )}
      {entityDialog?.entityType === 'dispenser' && (
        <DispenserDetailsDialog
          open
          onClose={closeEntityDialog}
          dispenserId={entityDialog.entityId}
          dispenserLabel={entityDialog.label}
        />
      )}
    </>
  );
};

TransactionListTable.propTypes = {
  refreshData: PropTypes.bool,
  isSales: PropTypes.bool,
  searchText: PropTypes.string,
  dateFilter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string
    })
  ]),
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tankFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  transactionTypeOverride: PropTypes.string,
};

export default TransactionListTable;
